import {defineStore} from 'pinia'
import {DoubleDatePickerType} from "@/components/global/AppDoubleDatePicker.vue";
import {addDays, formatDate, generateUniqueId, truncateWithEnd} from "@/utils/general-utils";
import {NotificationStatus, NotificationStatusText, NotificationType, NotificationTypeText} from "@/api";

export interface ActiveFilters {
  text: string;
  id: string;
  type: NotificationFiltersTypes;
  param: any;
}

export enum NotificationFiltersTypes {
  CREATION_DATE = 'creation-date',
  SEARCH = 'search',
  STATUS = 'status',
  TYPE = 'type',
  CURRENT_PAGE = 'current_page',
  COUNTRY_CODE = 'country-code',
}

export const useSearchNotificationHistoryStore = defineStore('searchNotificationHistory', {
  state: () => ({
    activeFilters: [] as ActiveFilters[],
  }),
  getters: {
    getParams(): string[] {
      return this.activeFilters.filter((filter) => filter.type !== NotificationFiltersTypes.CURRENT_PAGE).map((filter) => filter.param);
    },
    activeFiltersPreview(): ActiveFilters[] {
      return this.activeFilters.filter((f) => f.type !== NotificationFiltersTypes.CURRENT_PAGE);
    },
  },
  actions: {
    addFilter(filter: ActiveFilters): void {
      this.activeFilters.unshift(filter);
    },
    removeFilterType(type: NotificationFiltersTypes) {
      const indexes: number[] = [];
      this.activeFilters.forEach((filter, index) => {
        if (filter.type === type) {
          indexes.push(index);
        }
      });

      indexes.reverse().forEach((index) => {
        this.activeFilters.splice(index, 1);
      });
    },
    removeFilterWithId(idToRemove: string): void {
      const index = this.activeFilters.findIndex(({id}) => id == idToRemove);
      this.activeFilters.splice(index, 1);
    },
    resetFilters(): void {
      this.activeFilters = [];
    },

    addSearchFilter(query: string) {
      this.removeFilterType(NotificationFiltersTypes.SEARCH);
      this.addFilter({
        text: `Search : ${truncateWithEnd(query, 30)}`,
        id: generateUniqueId(),
        type: NotificationFiltersTypes.SEARCH,
        param: `&search=${encodeURIComponent(query.trim())}`,
      });
    },

    addStatusFilter(status: NotificationStatus[]) {
      status.forEach((s) => {
        this.addFilter({
          text: `Status : ${NotificationStatusText[s]}`,
          id: generateUniqueId(),
          type: NotificationFiltersTypes.STATUS,
          param: `&status[]=${encodeURIComponent(s)}`,
        });
      })
    },

    addTypeFilter(types: NotificationType[]) {
      types.forEach((t) => {
        this.addFilter({
          text: `Status : ${NotificationTypeText[t]}`,
          id: generateUniqueId(),
          type: NotificationFiltersTypes.TYPE,
          param: `&type[]=${encodeURIComponent(t)}`,
        });
      })
    },

    addCountryCodeFilter(countryCodes: string[]) {
      countryCodes.forEach((c) => {
        this.addFilter({
          text: `Country Code : ${c}`,
          id: generateUniqueId(),
          type: NotificationFiltersTypes.COUNTRY_CODE,
          param: `&countryCode[]=${encodeURIComponent(c)}`,
        });
      })
    },

    addCreationDateFilter(selectionType: DoubleDatePickerType, startDate: Date, endDate: Date) {
      let textValue = "";
      let apiParam = "";
      switch (selectionType) {
        case DoubleDatePickerType.SINGLE_DATE:
          textValue = "First creation date : " + startDate.toDateString();
          apiParam = `&creationDate[after]=${formatDate(startDate)}&creationDate[before]=${formatDate(addDays(startDate, 1))}`;
          break;
        case DoubleDatePickerType.DOUBLE_DATE_BEFORE:
          textValue = "Everything before : " + endDate.toDateString()
          apiParam = `&creationDate[before]=${formatDate(endDate)}`;
          break;
        case DoubleDatePickerType.DOUBLE_DATE:
          textValue = "Between " + startDate.toDateString() + " and " + endDate.toDateString();
          apiParam = `&creationDate[after]=${formatDate(startDate)}&creationDate[before]=${formatDate(endDate)}`;
          break;
        case DoubleDatePickerType.DOUBLE_DATE_AFTER:
          textValue = "Everything after : " + startDate.toDateString();
          apiParam = `&creationDate[after]=${formatDate(startDate)}`;
          break;
      }

      this.addFilter({
        text: textValue,
        id: generateUniqueId(),
        type: NotificationFiltersTypes.CREATION_DATE,
        param: apiParam,
      });

    },
  },
});
